<script lang="ts">
	import { onMount } from 'svelte';
	import { get } from 'svelte/store';
	//Formatters
	import { format } from 'svelte-i18n';
	//Design system components
	import { Alert, Button, Container, Loader, Modal, Radio, Text } from '@Shelly/shelly-design-system';
	//Helpers
	import { capFirstChar } from 'src/helpers/utilities';
	import {
		ALT_FIRMWARE_STAGES,
		getAltFirmware,
		updateAltFirmware,
	} from 'src/helpers/alternative_firmware_helpers';
	//Types
	import type { Device } from 'src/types/Device';
	import type { AlternativeFirmware } from 'src/types/DeviceStatusG2';
	import type { AddonType } from 'src/helpers/addons/addon_support_helpers';
	import type { AltFirmwareOption, AltFirmwareStage } from 'src/helpers/alternative_firmware_helpers';

	export let device: Device;
	export let selectedAddonType: AddonType;

	const getAltFirmwareOptions = (firmware: AlternativeFirmware | null): AltFirmwareOption[] => {
		const $format = get(format);
		return ALT_FIRMWARE_STAGES.map((stage) => {
			const buildId = firmware?.[stage]?.build_id || '';
			const version = firmware?.[stage]?.version || buildId;
			return {
				stage,
				title: capFirstChar(
					stage === 'beta'
						? $format('firmware_available_beta', {
								default: 'available beta version:',
							})
						: $format('firmware_available_stable', {
								default: 'available stable version:',
							}),
				),
				version,
			};
		}).filter((option) => option.version);
	};

	const selectAltFirmware = (stage: AltFirmwareStage) => {
		const option = altFirmwareOptions.find((item) => item.stage === stage);
		altFWStage = option?.stage || stage;
		altFirmwareVersion = option?.version || '';
	};

	const updateFirmware = async () => {
		waitForResponse = true;
		const res = await updateAltFirmware(device, selectedAddonType, altFirmware, altFWStage);
		if (res?.isok) {
			showModal = false;
		}
		waitForResponse = false;
	};

	const checkForUpdate = async () => {
		waitForCheckUpdate = true;
		altFirmware = await getAltFirmware(device, selectedAddonType);
		altFirmwareOptions = getAltFirmwareOptions(altFirmware);
		selectAltFirmware(altFirmwareOptions[0]?.stage || 'stable');
		altFirmwareName = altFirmware?.desc || altFirmware?.name || '';
		waitForCheckUpdate = false;
	};

	let altFirmware: AlternativeFirmware | null = null;
	let altFWStage: AltFirmwareStage = 'stable';
	let altFirmwareOptions: AltFirmwareOption[] = [];
	let altFirmwareVersion: string = '';
	let altFirmwareName: string = '';
	let waitForResponse = false;
	let waitForCheckUpdate = false;
	let showModal = false;
	let init = false;

	onMount(async () => {
		await checkForUpdate();
		init = true;
	});
</script>

{#if !init}
	<div class="d-flex justify-content-center">
		<Loader size="m" />
	</div>
{:else if altFirmwareVersion}
	<Alert
		type="info"
		class="mb-s"
		customIcon="fa-solid fa-arrows-rotate text-primary"
		isHeadingBold
		heading={$format('title_addon_firmware_update_required', {
			default: 'Firmware update required',
		})}
		message={$format('text_addon_firmware_update_required', {
			default: 'Switching to the selected Add-on requires firmware update.',
		})}
		cta={{
			title: $format('button_switch', {
				default: 'Switch',
			}),
		}}
		on:cta_click={() => (showModal = true)}
	/>
{:else}
	<Alert
		type="info"
		class="mb-s"
		customIcon="fa-solid fa-arrows-rotate text-primary"
		isHeadingBold
		heading={$format('title_addon_firmware_update_required', {
			default: 'Firmware update required',
		})}
		message={$format('text_addon_firmware_update_no_available', {
			default: 'No alternative firmware is currently available for this Add-on.',
		})}
	/>
	<Button
		size="m"
		class="width-full mb-s"
		loading={waitForCheckUpdate}
		disabled={waitForCheckUpdate}
		on:click={() => checkForUpdate()}
		>{capFirstChar(
			$format('button_check_for_updates', {
				default: 'check for updates',
			}),
		)}</Button
	>
{/if}

<Modal
	{showModal}
	on:close={() => (showModal = false)}
	title={$format('title_addon_firmware_update_required', {
		default: 'Firmware update required',
	})}
>
	<svelte:fragment slot="content">
		<Container padding="0">
			<Text class="mb-s">
				{$format('text_switch_addon_firmware', {
					default:
						'Switching to the selected Add-on requires a firmware update. By confirming, the device will install alternative firmware. This will delete all schedules and webhooks and reset the device.',
				})}
			</Text>
			<Text class="mb-m">
				{$format('text_switch_addon_firmware_after_update', {
					default:
						'After the update completes, return to this page, select the desired Add-on, and save to reboot.',
				})}
			</Text>
			{#if altFirmware}
				{#if altFirmwareOptions.length > 1}
					<Text class="mb-s" styleType="h2">
						{$format('label_select_firmware_version', {
							default: 'Select firmware version',
						})}
					</Text>
					<div class="d-flex flex-direction-column mb-s">
						{#each altFirmwareOptions as option}
							<Radio
								size="m"
								class="mb-xs"
								selected={altFWStage}
								value={option.stage}
								id={`radio-addon-fw-${option.stage}`}
								on:click={() => selectAltFirmware(option.stage)}
							>
								{`${option.title} ${option.version}`}
							</Radio>
						{/each}
					</div>
				{/if}
				<Alert
					class="mb-s"
					type="info"
					heading={$format('text_target_addon_firmware', {
						values: { name: altFirmwareName },
						default: 'Target firmware: {name}',
					})}
					message={$format('new_fw_ver_label', {
						values: { version: altFirmwareVersion },
						default: 'New firmware version: {version}',
					})}
				/>
			{/if}
			<Alert
				class="mb-s"
				type="warning"
				heading={$format('text_switch_addon_firmware_redirect', {
					default:
						'You will be redirected to Firmware update. After the firmware update is complete, return to Add-on peripherals and select the add-on again.',
				})}
			/>
		</Container>
	</svelte:fragment>
	<div class="d-flex justify-content-end" slot="footer">
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
			responsiveSize={{ sm: 'm' }}
			variant="secondary"
			on:click={() => (showModal = false)}
			>{$format('button_cancel', { default: 'Cancel' })}</Button
		>
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			responsiveSize={{ sm: 'm' }}
			disabled={waitForResponse || !altFirmware}
			loading={waitForResponse}
			on:click={() => updateFirmware()}
			>{$format('button_confirm', {
				default: 'Confirm',
			})}</Button
		>
	</div>
</Modal>
